/**
 * Mermaid Diagram Component
 * Renders Mermaid diagrams from text content
 */

import mermaid from 'mermaid';
import React, { useEffect, useRef, useState } from 'react';

import { normalizeUserFlowDirection } from './userFlowMermaid';

// Dark-mode native palette — vibrant, distinct colors for each tier
// No CSS invert() needed; designed to look great on dark backgrounds
mermaid.initialize({
  startOnLoad: false,
  theme: 'base',
  securityLevel: 'loose',
  fontFamily: 'Inter, system-ui, sans-serif',
  themeVariables: {
    // Primary – Cyan/Teal for Frontend/Presentation
    primaryColor: '#164E63',
    primaryTextColor: '#CFFAFE',
    primaryBorderColor: '#22D3EE',
    // Secondary – Emerald for Backend/Services
    secondaryColor: '#064E3B',
    secondaryTextColor: '#D1FAE5',
    secondaryBorderColor: '#34D399',
    // Tertiary – Amber/Gold for Data/Database
    tertiaryColor: '#78350F',
    tertiaryTextColor: '#FEF3C7',
    tertiaryBorderColor: '#FBBF24',
    // Background and general
    background: '#0F172A',
    mainBkg: '#1E293B',
    // Node defaults
    nodeBkg: '#1E293B',
    nodeBorder: '#38BDF8',
    // Flowchart clusters
    clusterBkg: '#1E293B',
    clusterBorder: '#475569',
    // Line/edge colors
    lineColor: '#94A3B8',
    // Text
    textColor: '#E2E8F0',
    // Sequence diagram
    actorBkg: '#164E63',
    actorBorder: '#22D3EE',
    actorTextColor: '#CFFAFE',
    signalColor: '#94A3B8',
    signalTextColor: '#E2E8F0',
    // ER diagram
    attributeBackgroundColorOdd: '#1E293B',
    attributeBackgroundColorEven: '#0F172A',
    // Labels and notes
    labelTextColor: '#E2E8F0',
    loopTextColor: '#E2E8F0',
    noteBkgColor: '#422006',
    noteTextColor: '#FEF3C7',
    noteBorderColor: '#F59E0B',
  },
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: 'basis',
    nodeSpacing: 50,
    rankSpacing: 50,
  },
  sequence: {
    useMaxWidth: true,
    actorMargin: 80,
    boxMargin: 10,
    boxTextMargin: 5,
    noteMargin: 10,
    messageMargin: 35,
  },
  er: {
    useMaxWidth: true,
    entityPadding: 15,
    stroke: '#475569',
    fill: '#1E293B',
  },
});

interface MermaidDiagramProps {
  content: string;
  id?: string;
  className?: string;
  renderOrder?: string | null;
  userFlow?: boolean;
  skipAnimation?: boolean;
  onAnimDone?: () => void;
}

const sanitizeMermaidId = (raw: string): string => raw.replace(/[^a-zA-Z0-9-]/g, '');

const LINK_STYLE_RE = /^\s*linkStyle\s+/i;
const LINK_STYLE_HEX_TERMINATOR_RE = /(stroke|fill):(#(?:[0-9a-fA-F]{3,8}));\s*$/;
const MERMAID_ERROR_SVG_RE = /syntax\s+error|parse\s+error|unknowndiagramerror|mermaid\s+version\s+\d/i;
const MERMAID_ERROR_ROLE_RE = /aria-roledescription\s*=\s*["']error["']/i;
const VISIBLE_DIAGRAM_ERROR = 'Diagram could not be rendered.';
const HTML_ENTITY_MAP: Record<string, string> = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  nbsp: ' ',
  quot: '"',
};
let mermaidRenderSequence = 0;
let mermaidRenderQueue: Promise<void> = Promise.resolve();
const mermaidSvgCache = new Map<string, string>();
const MERMAID_SVG_CACHE_LIMIT = 80;

const normalizeMermaidContent = (content: string): string => (
  content
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => (
      LINK_STYLE_RE.test(line)
        ? line.replace(LINK_STYLE_HEX_TERMINATOR_RE, '$1:$2')
        : line
    ))
    .join('\n')
    .trim()
);

const stripMermaidLinkStyles = (content: string): string => (
  content
    .split('\n')
    .filter((line) => !LINK_STYLE_RE.test(line))
    .join('\n')
    .trim()
);

const decodeBasicHtmlEntities = (value: string): string => value.replace(
  /&(#(\d+)|#x([0-9a-fA-F]+)|[a-zA-Z]+);/g,
  (entity, body: string, dec: string | undefined, hex: string | undefined) => {
    if (dec) return String.fromCodePoint(Number(dec));
    if (hex) return String.fromCodePoint(parseInt(hex, 16));

    return HTML_ENTITY_MAP[body.toLowerCase()] ?? entity;
  },
);

const textFromMarkup = (markup: string): string => decodeBasicHtmlEntities(
  markup
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
).replace(/\s+/g, ' ').trim();

const isMermaidErrorText = (text: string): boolean => MERMAID_ERROR_SVG_RE.test(text);

export const isMermaidErrorMarkup = (svg: string): boolean => (
  MERMAID_ERROR_ROLE_RE.test(svg) || isMermaidErrorText(svg) || isMermaidErrorText(textFromMarkup(svg))
);

const isMermaidErrorSvg = (svg: string): boolean => isMermaidErrorMarkup(svg);

export const hasMermaidErrorElement = (container: HTMLElement): boolean => (
  isMermaidErrorText(container.textContent || '') || isMermaidErrorMarkup(container.innerHTML)
);

const hasRenderedMermaidError = (container: HTMLElement): boolean => (
  hasMermaidErrorElement(container)
);

const tempMermaidContainerId = (renderId: string): string => `d${sanitizeMermaidId(renderId)}`;

const isMermaidTempContainer = (el: Element | null): el is HTMLElement => (
  el instanceof HTMLElement
  && /^d(?:ai-uf-|uf-|uf-fs-|arch-|arch-preview-|rich-|mermaid-diagram)/.test(el.id)
);

const removeMermaidErrorArtifacts = (renderId?: string) => {
  const doc = globalThis.document;

  if (!doc) return;

  if (renderId) {
    doc.getElementById(tempMermaidContainerId(renderId))?.remove();
  }

  doc.querySelectorAll('svg[aria-roledescription="error"]').forEach((svg) => {
    if (isMermaidTempContainer(svg.parentElement)) {
      svg.parentElement.remove();
    }
  });
};

const scheduleMermaidArtifactCleanup = (renderId: string) => {
  removeMermaidErrorArtifacts(renderId);
  globalThis.setTimeout(() => removeMermaidErrorArtifacts(renderId), 0);
  globalThis.requestAnimationFrame?.(() => removeMermaidErrorArtifacts(renderId));
};

const nextMermaidRenderId = (id: string, attemptIndex: number): string => {
  mermaidRenderSequence += 1;

  return `${sanitizeMermaidId(id)}-${Date.now()}-${mermaidRenderSequence}-${attemptIndex}`;
};

const enqueueMermaidRender = async (id: string, content: string, attemptIndex: number) => {
  const renderId = nextMermaidRenderId(id, attemptIndex);
  const renderTask = mermaidRenderQueue.then(async () => {
    try {
      return await mermaid.render(renderId, content);
    } finally {
      scheduleMermaidArtifactCleanup(renderId);
    }
  });

  mermaidRenderQueue = renderTask.then(
    () => undefined,
    () => undefined,
  );

  return renderTask;
};

const mermaidCacheKey = (id: string, content: string): string => `${sanitizeMermaidId(id)}:${normalizeMermaidContent(content)}`;

const cacheMermaidSvg = (key: string, svg: string) => {
  if (mermaidSvgCache.size >= MERMAID_SVG_CACHE_LIMIT) {
    const oldestKey = mermaidSvgCache.keys().next().value;

    if (oldestKey) mermaidSvgCache.delete(oldestKey);
  }

  mermaidSvgCache.set(key, svg);
};

// 6-color palette for per-participant differentiation
const ACTOR_PALETTE = [
  { fill: '#164E63', stroke: '#22D3EE' },  // cyan
  { fill: '#064E3B', stroke: '#34D399' },  // emerald
  { fill: '#4C1D95', stroke: '#A78BFA' },  // violet
  { fill: '#78350F', stroke: '#FBBF24' },  // amber
  { fill: '#7F1D1D', stroke: '#FB7185' },  // rose
  { fill: '#1E3A5F', stroke: '#60A5FA' },  // blue
];

const colorSequenceActors = (container: HTMLElement) => {
  const actorRects = container.querySelectorAll('rect.actor');

  if (actorRects.length === 0) return;

  // Each participant has 2 rects (top + bottom box)
  const participantCount = Math.ceil(actorRects.length / 2);

  actorRects.forEach((rect, i) => {
    const participantIndex = i < participantCount ? i : i - participantCount;
    const color = ACTOR_PALETTE[participantIndex % ACTOR_PALETTE.length];

    (rect as HTMLElement).style.fill = color.fill;
    (rect as HTMLElement).style.stroke = color.stroke;
  });

  // Color actor text labels to stay readable
  const actorTexts = container.querySelectorAll('text.actor');

  actorTexts.forEach((text) => {
    (text as HTMLElement).style.fill = '#E2E8F0';
  });
};

const colorErEntityBoxes = (container: HTMLElement) => {
  const entityBoxes = container.querySelectorAll('.entityBox');

  if (entityBoxes.length === 0) return;

  entityBoxes.forEach((box, i) => {
    const color = ACTOR_PALETTE[i % ACTOR_PALETTE.length];

    (box as HTMLElement).style.fill = color.fill;
    (box as HTMLElement).style.stroke = color.stroke;
  });
};

const colorErAttributes = (container: HTMLElement) => {
  const entityLabels = container.querySelectorAll('.entityLabel text');

  entityLabels.forEach((label) => {
    (label as HTMLElement).style.fill = '#E2E8F0';
  });

  const attrOdd = container.querySelectorAll('.attributeBoxOdd');
  const attrEven = container.querySelectorAll('.attributeBoxEven');

  attrOdd.forEach((box) => {
    (box as HTMLElement).style.fill = '#1E293B';
    (box as HTMLElement).style.stroke = '#334155';
  });

  attrEven.forEach((box) => {
    (box as HTMLElement).style.fill = '#0F172A';
    (box as HTMLElement).style.stroke = '#334155';
  });
};

const extractPageName = (el: HTMLElement): string | null => {
  const text = el.querySelector('.nodeLabel')?.textContent?.trim() || '';
  const colonIdx = text.indexOf(':');

  return colonIdx > 0 ? text.substring(0, colonIdx).trim() : null;
};

const applyPageColor = (node: HTMLElement, color: typeof ACTOR_PALETTE[0]) => {
  const rect = node.querySelector('rect, polygon, circle') as HTMLElement | null;

  if (rect) { rect.style.fill = color.fill; rect.style.stroke = color.stroke; }

  const label = node.querySelector('.nodeLabel') as HTMLElement | null;

  if (label) label.style.color = '#E2E8F0';
};

const colorFlowchartByPage = (container: HTMLElement) => {
  const nodes = container.querySelectorAll('.node');

  if (nodes.length === 0) return;

  const pageColorMap = new Map<string, typeof ACTOR_PALETTE[0]>();
  let nextIdx = 0;

  nodes.forEach((node) => {
    const page = extractPageName(node as HTMLElement);

    if (!page) return;

    if (!pageColorMap.has(page)) {
      pageColorMap.set(page, ACTOR_PALETTE[nextIdx % ACTOR_PALETTE.length]);
      nextIdx += 1;
    }

    applyPageColor(node as HTMLElement, pageColorMap.get(page)!);
  });
};

const isOperationalFlow = (renderOrder?: string | null): boolean => {
  try {
    const metadata = JSON.parse(renderOrder || 'null') as { planning_metadata?: { flow_kind?: string } } | null;

    return ['operational_workflow', 'system_workflow'].includes(metadata?.planning_metadata?.flow_kind || '');
  } catch { return false; }
};

const colorOperationalFlow = (container: HTMLElement, renderOrder?: string | null) => {
  if (!isOperationalFlow(renderOrder)) return;

  // Operational actions have no page prefix; reuse the existing per-node palette.
  container.querySelectorAll('.node').forEach((node, index) => {
    applyPageColor(node as HTMLElement, ACTOR_PALETTE[index % ACTOR_PALETTE.length]);
  });
};

const animateSolidEdges = (container: HTMLElement) => {
  const edges = container.querySelectorAll('.flowchart-link');

  edges.forEach((edge) => {
    if (!edge.getAttribute('stroke-dasharray') || edge.getAttribute('stroke-dasharray') === '0') {
      edge.classList.add('edge-animated');
    }
  });

  const msgLines = container.querySelectorAll('.messageLine0, .messageLine1');

  msgLines.forEach((line) => {
    line.classList.add('edge-animated');
  });
};

const REVEAL_DELAY = 80;


const getChildRectY = (el: HTMLElement): number | null => {
  const childRect = el.querySelector('rect');
  const y = childRect?.getAttribute('y');

  return y ? parseFloat(y) : null;
};

const getPathY = (el: HTMLElement): number => {
  const directY = el.getAttribute('y') || getChildRectY(el);

  if (directY !== null) return typeof directY === 'string' ? parseFloat(directY) : directY;

  const d = el.getAttribute('d');

  if (!d) return 0;

  const nums = d.match(/[\d.-]+/g);

  return nums && nums.length > 1 ? parseFloat(nums[1]) : 0;
};

const getSvgY = (el: HTMLElement): number => {
  const t = el.getAttribute('transform') || '';
  const m = t.match(/translate\(\s*([\d.-]+)[,\s]+([\d.-]+)/);

  return m ? parseFloat(m[2]) : getPathY(el);
};

const matchEdge = (el: HTMLElement, id: string): boolean => {
  const parts = id.slice(5).split('-');

  if (parts.length < 2) return false;

  const cls = el.getAttribute('class') || '';

  return cls.includes(`LS-${parts[0]}`) && cls.includes(`LE-${parts[1]}`);
};

const matchCluster = (el: HTMLElement, id: string): boolean => {
  const clusterId = id.slice(8);

  return el.id === clusterId || el.id.endsWith(`-${clusterId}`);
};

const matchNode = (el: HTMLElement, id: string): boolean => {
  const dataId = el.getAttribute('data-id');

  if (dataId) return dataId === id;

  // Exclude edge paths — their ids (e.g. "L-Browser-React-0") contain node names
  if ((el.getAttribute('class') || '').includes('flowchart-link')) return false;

  return !!el.id && el.id.includes(id);
};

const matchElement = (el: HTMLElement, id: string): boolean => {
  if (id.startsWith('edge-')) return matchEdge(el, id);
  if (id.startsWith('cluster-')) return matchCluster(el, id);

  return matchNode(el, id);
};

const deferEdges = (order: string[]): string[] => {
  const result = order.filter((id) => !id.startsWith('edge-'));
  const edges = order.filter((id) => id.startsWith('edge-'));

  edges.forEach((edgeId) => {
    const parts = edgeId.slice(5).split('-');
    const srcIdx = result.indexOf(parts[0]);
    const tgtIdx = result.indexOf(parts[1]);
    const afterIdx = Math.max(srcIdx, tgtIdx);

    result.splice(afterIdx + 1, 0, edgeId);
  });

  return result;
};

const orderByPrecomputed = (elements: HTMLElement[], order: string[]): HTMLElement[] => {
  const ordered: HTMLElement[] = [];
  const remaining = new Set(elements);
  const deferred = deferEdges(order);

  deferred.forEach((id) => {
    const match = elements.find((el) => remaining.has(el) && matchElement(el, id));

    if (match) {
      ordered.push(match);
      remaining.delete(match);
    }
  });

  remaining.forEach((el) => { ordered.push(el); });

  return ordered;
};

const tryPrecomputed = (all: HTMLElement[], renderOrder: string): HTMLElement[] | null => {
  try {
    const parsed: unknown = JSON.parse(renderOrder);
    const order = Array.isArray(parsed)
      ? parsed
      : (
        parsed
        && typeof parsed === 'object'
        && Array.isArray((parsed as { order?: unknown }).order)
          ? (parsed as { order: unknown[] }).order
          : null
      );

    return order
      ? orderByPrecomputed(all, order.map((item) => String(item)))
      : null;
  } catch { return null; }
};

const collectRevealTargets = (container: HTMLElement, renderOrder?: string | null) => {
  const sel = '.node, .cluster, .entityBox, .flowchart-link, .edgeLabel, rect.actor, .messageLine0, .messageLine1';
  const all = Array.from(container.querySelectorAll(sel)) as HTMLElement[];

  if (renderOrder) {
    const precomputed = tryPrecomputed(all, renderOrder);

    if (precomputed) return precomputed;
  }

  all.sort((a, b) => getSvgY(a) - getSvgY(b));

  return all;
};

const isEdgeElement = (el: HTMLElement): boolean => {
  const cls = el.getAttribute('class') || '';

  return cls.includes('flowchart-link') || cls.includes('messageLine');
};

const FADE_DURATION = '0.6s';

const injectFadeStyle = (container: HTMLElement) => {
  const svg = container.querySelector('svg');

  if (!svg || svg.querySelector('.diagram-fade-style')) return;

  const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');

  style.setAttribute('class', 'diagram-fade-style');
  style.textContent = '@keyframes diagramFadeIn { from { opacity: 0; } to { opacity: 1; } }';
  svg.prepend(style);
};

const hideAllElements = (elements: readonly HTMLElement[]) => {
  elements.forEach((el) => { el.style.opacity = '0'; });
};

const DRAW_DURATION_MIN = 0.6;
const DRAW_DURATION_MAX = 1.6;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const safeGetLength = (el: any): number => {
  try { return el.getTotalLength(); } catch { return 0; }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const computeDrawDuration = (pathEl: any): number => {
  const len = safeGetLength(pathEl);

  return Math.min(DRAW_DURATION_MAX, Math.max(DRAW_DURATION_MIN, len / 200));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const setupDrawState = (pathEl: any, len: number) => {
  pathEl.style.setProperty('stroke-dasharray', `${len}`, 'important');
  pathEl.style.setProperty('stroke-dashoffset', `${len}`, 'important');
  pathEl.style.opacity = '1';
  pathEl.getBoundingClientRect();
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const triggerDrawAnimation = (pathEl: any, duration: number) => {
  pathEl.style.setProperty('transition', `stroke-dashoffset ${duration}s ease-in-out`);
  pathEl.style.setProperty('stroke-dashoffset', '0', 'important');
};

const cleanupDrawAnimation = (pathEl: HTMLElement, durationMs: number) => {
  setTimeout(() => {
    pathEl.style.removeProperty('stroke-dasharray');
    pathEl.style.removeProperty('stroke-dashoffset');
    pathEl.style.removeProperty('transition');
    pathEl.classList.add('edge-animated');
  }, durationMs + 50);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const findPathElement = (el: HTMLElement): any => {
  const tag = el.tagName.toLowerCase();

  if (tag === 'path' || tag === 'line') return el;

  return el.querySelector('path, line');
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const runDrawSequence = (pathEl: any, len: number, duration: number) => {
  setupDrawState(pathEl, len);
  triggerDrawAnimation(pathEl, duration);
  cleanupDrawAnimation(pathEl, duration * 1000);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const animateEdgeDraw = (pathEl: any) => {
  const len = safeGetLength(pathEl);

  if (len === 0) {
    pathEl.classList.add('edge-animated');

    return;
  }

  runDrawSequence(pathEl, len, computeDrawDuration(pathEl));
};

const revealEdgeWithDraw = (el: HTMLElement) => {
  const pathEl = findPathElement(el);

  el.style.opacity = '1';

  if (!pathEl || typeof pathEl.getTotalLength !== 'function') {
    el.classList.add('edge-animated');

    return;
  }

  animateEdgeDraw(pathEl);
};

const revealElement = (el: HTMLElement) => {
  if (isEdgeElement(el)) {
    revealEdgeWithDraw(el);

    return;
  }

  el.style.opacity = '1';
  el.style.animation = `diagramFadeIn ${FADE_DURATION} ease-in`;
};

const scheduleElementReveals = (elements: readonly HTMLElement[]): number => {
  elements.forEach((el, i) => {
    setTimeout(() => { revealElement(el as HTMLElement); }, i * REVEAL_DELAY);
  });

  return elements.length * REVEAL_DELAY + 600;
};

const setupAndRunReveal = (container: HTMLElement, renderOrder?: string | null): number => {
  const targets = collectRevealTargets(container, renderOrder) as unknown as HTMLElement[];

  injectFadeStyle(container);
  hideAllElements(targets);
  container.style.opacity = '1';

  return scheduleElementReveals(targets);
};

const isContainerHidden = (el: HTMLElement): boolean => {
  const rect = el.getBoundingClientRect();

  return rect.width === 0 && rect.height === 0;
};

const deferIfHidden = (container: HTMLElement, svg: HTMLElement): boolean => {
  if (!isContainerHidden(container)) return false;

  svg.dataset.pendingReveal = 'true';
  container.style.opacity = '1';

  return true;
};

const handleRevealAndEdges = (container: HTMLElement, renderOrder?: string | null): number => {
  const svg = container.querySelector('svg') as HTMLElement | null;

  if (!svg || (svg.dataset && svg.dataset.revealed)) {
    animateSolidEdges(container);

    return 0;
  }

  // If the container is hidden (display:none), defer animation until visible.
  // Return a small min duration so onAnimDone isn't fired synchronously.
  if (deferIfHidden(container, svg)) return 800;

  svg.dataset.revealed = 'true';

  return setupAndRunReveal(container, renderOrder);
};

const applyColors = (c: HTMLElement, renderOrder?: string | null) => {
  colorSequenceActors(c); colorErEntityBoxes(c); colorErAttributes(c); colorFlowchartByPage(c);
  colorOperationalFlow(c, renderOrder);
};

const postProcessDiagram = (container: HTMLElement, renderOrder?: string | null, skipAnimation?: boolean): number => {
  applyColors(container, renderOrder);
  if (skipAnimation) {
    container.style.opacity = '1';

    return 0;
  }

  return handleRevealAndEdges(container, renderOrder);
};

const renderMermaidSvg = async (id: string, content: string) => {
  const cacheKey = mermaidCacheKey(id, content);
  const cachedSvg = mermaidSvgCache.get(cacheKey);

  if (cachedSvg) {
    if (!isMermaidErrorSvg(cachedSvg)) return cachedSvg;
    mermaidSvgCache.delete(cacheKey);
  }

  const normalized = normalizeMermaidContent(content);
  const withoutLinkStyles = stripMermaidLinkStyles(normalized);
  const attempts = withoutLinkStyles && withoutLinkStyles !== normalized
    ? [normalized, withoutLinkStyles]
    : [normalized];
  let lastError: unknown = null;

  for (let index = 0; index < attempts.length; index += 1) {
    const attempt = attempts[index];

    try {
      const { svg } = await enqueueMermaidRender(id, attempt, index);

      if (isMermaidErrorSvg(svg)) {
        lastError = new Error('Mermaid returned a syntax error diagram');
        continue;
      }

      cacheMermaidSvg(cacheKey, svg);

      return svg;
    } catch (err) {
      lastError = err;
      await new Promise((r) => { setTimeout(r, 150); });
    }
  }

  throw lastError || new Error('Failed to render Mermaid diagram');
};

const handleRenderError = (
  err: unknown,
  containerRef: React.RefObject<HTMLDivElement | null>
) => {
  console.error('Mermaid render error:', err);

  if (containerRef.current) {
    containerRef.current.style.opacity = '1';
    containerRef.current.replaceChildren();
    containerRef.current.setAttribute('data-render-error', 'true');
  }
  removeMermaidErrorArtifacts();

  return VISIBLE_DIAGRAM_ERROR;
};

const renderDiagramContent = async (
  content: string,
  id: string,
  containerRef: React.RefObject<HTMLDivElement | null>,
  renderOrder?: string | null,
  skipAnimation?: boolean,
  isCancelled?: () => boolean,
): Promise<number> => {
  removeMermaidErrorArtifacts();
  containerRef.current!.innerHTML = '';
  containerRef.current!.style.opacity = '0';
  containerRef.current!.removeAttribute('data-render-error');

  const svg = await renderMermaidSvg(id, content);

  if (isCancelled?.()) return 0;

  if (isMermaidErrorSvg(svg)) {
    throw new Error('Mermaid returned a syntax error diagram');
  }

  containerRef.current!.innerHTML = svg;

  if (hasRenderedMermaidError(containerRef.current!)) {
    throw new Error('Mermaid rendered a syntax error diagram');
  }

  return postProcessDiagram(containerRef.current!, renderOrder, skipAnimation);
};

const executeDiagramRender = async (
  content: string,
  id: string,
  containerRef: React.RefObject<HTMLDivElement | null>,
  setError: (err: string | null) => void,
  renderOrder?: string | null,
  skipAnimation?: boolean,
  isCancelled?: () => boolean,
): Promise<number> => {
  try {
    return await renderDiagramContent(content, id, containerRef, renderOrder, skipAnimation, isCancelled);
  } catch (err) {
    if (isCancelled?.()) return 0;

    const errorMsg = handleRenderError(err, containerRef);

    setError(errorMsg);

    return 0;
  }
};

const performRender = async (
  containerRef: React.RefObject<HTMLDivElement | null>,
  content: string,
  id: string,
  setError: (err: string | null) => void,
  setIsLoading: (loading: boolean) => void,
  renderOrder?: string | null,
  skipAnimation?: boolean,
  isCancelled?: () => boolean,
): Promise<number> => {
  setIsLoading(true);
  setError(null);

  const dur = await executeDiagramRender(content, id, containerRef, setError, renderOrder, skipAnimation, isCancelled);

  if (isCancelled?.()) return 0;

  setIsLoading(false);

  return dur;
};

const tryPendingReveal = (el: HTMLElement, renderOrder?: string | null) => {
  const svg = el.querySelector('svg') as HTMLElement | null;

  if (!svg?.dataset?.pendingReveal || isContainerHidden(el)) return;

  delete svg.dataset.pendingReveal;
  svg.dataset.revealed = 'true';
  setupAndRunReveal(el, renderOrder);
};

/** Replay the staggered reveal for an already-revealed diagram that just became visible. */
const replayReveal = (el: HTMLElement, renderOrder?: string | null) => {
  const svg = el.querySelector('svg') as HTMLElement | null;

  if (!svg?.dataset?.revealed || svg.dataset.pendingReveal || isContainerHidden(el)) return;

  // Strip edge-animated classes so draw animations replay cleanly
  el.querySelectorAll('.edge-animated').forEach((e) => e.classList.remove('edge-animated'));

  delete svg.dataset.revealed;
  svg.dataset.revealed = 'true';
  setupAndRunReveal(el, renderOrder);
};

const fireAnimDone = (cbRef?: React.RefObject<(() => void) | undefined>, dur = 0) => {
  const delay = dur > 0 ? dur : (cbRef?.current ? 100 : 0);

  if (delay > 0) { setTimeout(() => cbRef?.current?.(), delay); } else { cbRef?.current?.(); }
};

const runRenderEffect = async (
  containerRef: React.RefObject<HTMLDivElement | null>,
  content: string, id: string,
  setError: (e: string | null) => void, setIsLoading: (v: boolean) => void,
  renderOrder?: string | null, skipAnimation?: boolean,
  cbRef?: React.RefObject<(() => void) | undefined>,
  isCancelled?: () => boolean,
) => {
  if (!containerRef.current || !content) {
    if (isCancelled?.()) return;
    setIsLoading(false); fireAnimDone(cbRef);

    return;
  }

  const dur = await performRender(containerRef, content, id, setError, setIsLoading, renderOrder, skipAnimation, isCancelled);

  if (isCancelled?.()) return;

  fireAnimDone(cbRef, dur);
};

const useVisibilityReveal = (
  containerRef: React.RefObject<HTMLDivElement | null>, renderOrder?: string | null,
) => {
  useEffect(() => {
    const el = containerRef.current;

    if (!el) return undefined;

    const onTabSwitch = () => {
      globalThis.requestAnimationFrame(() => {
        tryPendingReveal(el, renderOrder);
        replayReveal(el, renderOrder);
      });
    };

    window.addEventListener('navigate_to_tab', onTabSwitch);

    const observer = new globalThis.IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) tryPendingReveal(el, renderOrder); },
      { threshold: 0.01 },
    );

    observer.observe(el);

    return () => { window.removeEventListener('navigate_to_tab', onTabSwitch); observer.disconnect(); };
  }, [containerRef, renderOrder]);
};

const useDiagramRenderer = (
  content: string, id: string,
  containerRef: React.RefObject<HTMLDivElement | null>,
  renderOrder?: string | null, skipAnimation?: boolean, onAnimDone?: () => void,
) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const cbRef = useRef(onAnimDone);

  useEffect(() => { cbRef.current = onAnimDone; }, [onAnimDone]);

  useEffect(() => {
    let cancelled = false;

    runRenderEffect(
      containerRef,
      content,
      id,
      setError,
      setIsLoading,
      renderOrder,
      skipAnimation,
      cbRef,
      () => cancelled,
    );

    return () => { cancelled = true; };
  }, [content, id, containerRef, renderOrder, skipAnimation]);

  useVisibilityReveal(containerRef, renderOrder);

  return { error, isLoading };
};

const DiagramContent: React.FC<{
  isLoading: boolean;
  error: string | null;
  containerRef: React.RefObject<HTMLDivElement | null>;
}> = ({ isLoading, error, containerRef }) => (
  <>
    {isLoading && (
      <div className="mermaid-loading">
        <span>Rendering diagram...</span>
      </div>
    )}
    {error && (
      <div className="mermaid-error">
        <span>Diagram Error: {error}</span>
      </div>
    )}
    <div ref={containerRef} className="mermaid-content" />
  </>
);

export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({
  content,
  id = 'mermaid-diagram',
  className = '',
  renderOrder,
  userFlow = false,
  skipAnimation,
  onAnimDone,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const renderedContent = userFlow ? normalizeUserFlowDirection(content) : content;
  const { error, isLoading } = useDiagramRenderer(
    renderedContent, id, containerRef, renderOrder, skipAnimation, onAnimDone,
  );
  const stateClassName = isLoading ? ' mermaid-container--loading' : '';

  if (!content) {
    return (
      <div className={`mermaid-container mermaid-empty ${className}`}>
        <p>No diagram content available</p>
      </div>
    );
  }

  return (
    <div className={`mermaid-container${stateClassName} ${className}`}>
      <DiagramContent isLoading={isLoading} error={error} containerRef={containerRef} />
    </div>
  );
};
